import React from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from '../../../../../utils/formatting';
import Img from '../../../../Img';
import styles from '../activityStats.scss';

const LOCK_BADGE_SRC = '/img/v2/icons/icon-feature-lock.svg';

function ActivityStatCard({
    title,
    mobileTitle,
    total,
    wins,
    losses,
    rank,
    rankDisplay,
    rankLabel,
    teaser,
    hideStatHeader,
    rankInfoText,
    locked,
    onLockedClick,
    'data-testid': dataTestId,
}) {
    const cardClassName = teaser ? `${styles.statCard} ${styles.statCardTeaser}` : styles.statCard;

    const renderHeader = () => {
        if (hideStatHeader) { return null; }
        return (
            <div className={styles.statCardHeader} data-testid={dataTestId && `${dataTestId}.title`}>
                <span className={styles.statCardTitleDesktop} data-testid={dataTestId && `${dataTestId}.title-desktop`}>{title}</span>
                <span className={styles.statCardTitleMobile} data-testid={dataTestId && `${dataTestId}.title-mobile`}>{mobileTitle || title}</span>
            </div>
        );
    };

    if (locked) {
        return (
            <button
                type="button"
                className={`${cardClassName} ${styles.statCardLocked}`}
                onClick={onLockedClick}
                data-testid={dataTestId}
            >
                {renderHeader()}
                <div className={`${styles.statCardBody} ${styles.statCardLockedBody}`}>
                    <Img
                        src={LOCK_BADGE_SRC}
                        alt="Locked"
                        className={styles.statCardLockBadge}
                        data-testid={dataTestId && `${dataTestId}.lock`}
                    />
                </div>
            </button>
        );
    }

    return (
        <div className={cardClassName} data-testid={dataTestId}>
            {renderHeader()}
            <div className={styles.statCardBody}>
                <div className={styles.statCardTopGroup}>
                    <div className={styles.statCardTotal} data-testid={dataTestId && `${dataTestId}.total`}>{formatNumber(total)}</div>
                    <div className={styles.statCardDivider} />
                    <div className={styles.statCardWinLoss}>
                        <div className={styles.statCardWinCol}>
                            <span className={styles.statCardWinValue} data-testid={dataTestId && `${dataTestId}.wins-value`}>{formatNumber(wins)}</span>
                            <span className={styles.statCardWinLabel} data-testid={dataTestId && `${dataTestId}.wins-label`}>WINS</span>
                        </div>
                        <div className={styles.statCardWlDivider} />
                        <div className={styles.statCardLossCol}>
                            <span className={styles.statCardLossValue} data-testid={dataTestId && `${dataTestId}.losses-value`}>{formatNumber(losses)}</span>
                            <span className={styles.statCardLossLabel} data-testid={dataTestId && `${dataTestId}.losses-label`}>LOSSES</span>
                        </div>
                    </div>
                </div>
                {(rank != null || rankDisplay != null) && (
                    <React.Fragment>
                        <div className={styles.statCardDivider} />
                        <div className={styles.statCardRank}>
                            {rankLabel && <span className={styles.statCardRankLabel} data-testid={dataTestId && `${dataTestId}.rank-label`}>{rankLabel}</span>}
                            <span className={styles.statCardRankValue} data-testid={dataTestId && `${dataTestId}.rank-value`}>{rank === null || rank === undefined ? rankDisplay : formatNumber(rank)}</span>
                            {rankInfoText && <span className={styles.statCardRankInfo} data-testid={dataTestId && `${dataTestId}.rank-info`}>{rankInfoText}</span>}
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
}

ActivityStatCard.propTypes = {
    title: PropTypes.string.isRequired,
    mobileTitle: PropTypes.string,
    total: PropTypes.number,
    wins: PropTypes.number,
    losses: PropTypes.number,
    rank: PropTypes.number,
    rankDisplay: PropTypes.string,
    rankLabel: PropTypes.string,
    teaser: PropTypes.bool,
    hideStatHeader: PropTypes.bool,
    rankInfoText: PropTypes.string,
    locked: PropTypes.bool,
    onLockedClick: PropTypes.func,
    'data-testid': PropTypes.string,
};

ActivityStatCard.defaultProps = {
    mobileTitle: null,
    total: 0,
    wins: 0,
    losses: 0,
    rank: null,
    rankDisplay: null,
    rankLabel: null,
    teaser: false,
    hideStatHeader: false,
    rankInfoText: null,
    locked: false,
    onLockedClick: null,
    'data-testid': null,
};

export default ActivityStatCard;
